import React from 'react';
import classnames from 'classnames';
import type {IconName, IconPrefix} from '@fortawesome/fontawesome-common-types';

import {Icon} from '@albert-io/atomic';

interface IllustrationProps {
  icon: IconName | [IconPrefix, IconName];
  /** Icon badge colors (e.g. 'background-clementine-subtle content-clementine'); brand by default. */
  badgeClassName?: string;
}

// A ghost list row: icon badge + two skeleton bars, mirroring a real row.
const SkeletonCard = ({icon, badgeClassName}: IllustrationProps) => (
  <div className='gap-x1-half rounded-x1-half border-subtle background-primary p-x1 flex w-[232px] items-center border'>
    <div
      className={classnames(
        'flex h-x4-half w-x4-half shrink-0 items-center justify-center rounded-full',
        badgeClassName ?? 'background-brand-subtle-hover'
      )}
    >
      <Icon
        className='text-s'
        color={badgeClassName ? 'inherit' : 'brand'}
        icon={icon}
        iconStyle='regular'
      />
    </div>
    <div className='gap-x0-half flex grow flex-col'>
      <div className='h-x1 background-bold w-[100px] rounded-full' />
      <div className='h-x1 w-x5 background-bold rounded-full' />
    </div>
  </div>
);

interface EmptyIllustrationProps extends IllustrationProps {
  /** Overrides the icon on the front (offset) ghost row, for a state covering two kinds of row. */
  frontIcon?: IconName | [IconPrefix, IconName];
}

/** Two overlapping ghost rows — the decorative illustration above empty-state copy. */
export const EmptyIllustration = ({icon, frontIcon, badgeClassName}: EmptyIllustrationProps) => (
  <div aria-hidden='true' className='grid place-items-start'>
    <div className='col-start-1 row-start-1'>
      <SkeletonCard badgeClassName={badgeClassName} icon={icon} />
    </div>
    <div className='mt-x4 ml-x4 rounded-x1-half elevation-shadow-l2 col-start-1 row-start-1'>
      <SkeletonCard badgeClassName={badgeClassName} icon={frontIcon ?? icon} />
    </div>
  </div>
);

interface Props {
  illustration: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  layout?: 'stacked' | 'inline';
}

/**
 * Dashboard empty state: a decorative illustration, copy (children), and an optional action.
 * Shared by the Assignments, People (co-teachers), Folders, and adaptive empty states.
 */
export const EmptyState = ({illustration, action, children, layout = 'stacked'}: Props) =>
  layout === 'inline' ? (
    <div className='gap-x5 flex w-full flex-wrap items-start'>
      {illustration}
      <div className='gap-x2 flex min-w-[280px] flex-1 flex-col items-start'>
        {children}
        {action}
      </div>
    </div>
  ) : (
    <div className='gap-x3 py-x8 flex w-full flex-col items-center text-center'>
      {illustration}
      {children}
      {action}
    </div>
  );
